import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { admissionsStats, createAdmitsTier, listAdmitsTiers, setTierAdmits } from "@/api/admits-seva-album";

/**
 * Tickets that admit several people: a "family of four" kind is one ticket,
 * one scan at the door, and four people in plates, capacity and the counts.
 */
export function AdmitsPanel({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const [name, setName] = useState("Family of four");
  const [admits, setAdmits] = useState(4);
  const [msg, setMsg] = useState<string | null>(null);
  const tiers = useQuery({ queryKey: ["admits-tiers", eventId], queryFn: () => listAdmitsTiers(eventId) });
  const stats = useQuery({ queryKey: ["admits-stats", eventId], queryFn: () => admissionsStats(eventId) });

  async function run(fn: () => Promise<unknown>, done: string) {
    setMsg(null);
    try {
      await fn();
      setMsg(done);
      await qc.invalidateQueries({ queryKey: ["admits-tiers", eventId] });
      await qc.invalidateQueries({ queryKey: ["admits-stats", eventId] });
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "That did not work");
    }
  }

  const s = stats.data?.data;
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-medium text-gray-700">Tickets that admit several people</h3>
      <ul className="text-sm text-gray-600 space-y-1">
        {(tiers.data?.data.tiers ?? []).map((t) => (
          <li key={t.id} className="flex gap-2 items-center">
            <span>
              {t.name} · admits {t.admitsCount} · {t.quantitySold} sold
              {t.isActive ? "" : " · off sale"}
            </span>
            <button className="text-xs underline" onClick={() => run(() => setTierAdmits(eventId, t.id, t.admitsCount + 1), "Changed.")}>
              admits +1
            </button>
          </li>
        ))}
      </ul>
      <div className="flex gap-2 flex-wrap items-center text-sm">
        <input className="input w-40" value={name} onChange={(e) => setName(e.target.value)} />
        <label className="text-xs text-gray-600">
          admits{" "}
          <input className="input w-16" type="number" min={1} max={20} value={admits} onChange={(e) => setAdmits(Number(e.target.value))} />
        </label>
        <button
          className="px-3 py-1 rounded border border-gray-300"
          onClick={() => run(() => createAdmitsTier(eventId, { name, priceCents: 0, quantityTotal: 50, admitsCount: admits }), "Added.")}
        >
          Add free kind
        </button>
      </div>
      {s && (
        <p className="text-xs text-gray-600">
          At the door: {s.totalCheckedIn} of {s.totalIssued} tickets · {s.admissions.checkedIn} of {s.admissions.issued} people
        </p>
      )}
      {msg && <p className="text-xs text-gray-600">{msg}</p>}
    </div>
  );
}
